import { ReactNode } from 'react'
import { Outlet } from 'react-router-dom'
import { com } from '@eidu/web'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from 'react-oidc-context'
import { Alert, AppShell, Button, Collapse, iconSizes } from '@common/ui'
import {
  IconBrain,
  IconHistoryToggle,
  IconList,
  IconLock,
  IconShieldLock,
  IconTopologyStar3,
  IconWorld,
} from '@common/ui/icons'
import useEntityTypes, { useSortedEntityTypes } from '../io/useEntityTypes'
import { entityTypeKeys } from '../io/queryKeys'
import NavbarSections, { NavItem, NavSection } from '../components/layout/NavbarSections'
import TopAppBar from '../components/layout/TopAppBar'
import { useCompactNavigation } from '../components/layout/CompactNavigationContext'
import { extractProfileClaims } from '../io/api/authorization/profileClaims'
import { iconForEntityTypeKind } from './entity/kindViewRegistry'
import { isAdminUserType } from '../util/entityType/isAdminUserType'
import useHasGlobalPermissions from './permission/useHasGlobalPermissions'
import { buildSidebarSectionsFromEntityTypes } from '../components/navigation/SidebarSection'
import type { SidebarGroup } from '../domain/kindRegistry'
import GlobalAction = com.eidu.entity.permission.GlobalAction
import EntityTypeKind = com.eidu.entity.type.EntityTypeKind
import EntityType = com.eidu.entity.type.EntityType
import entityIdFromString = com.eidu.entity.entityIdFromString

const SIDEBAR_GROUP_ORDER: readonly SidebarGroup[] = ['entity', 'document', 'asset']

const LandingPage = () => {
  const queryClient = useQueryClient()
  const auth = useAuth()
  const { drawerOpen, closeDrawer } = useCompactNavigation()

  const { userEntityId } = auth.user?.profile ? extractProfileClaims(auth.user.profile) : { userEntityId: undefined }

  const { hasPermission: hasGlobalPermission, error: fetchPermissionsError } = useHasGlobalPermissions(
    GlobalAction.ManageForms,
    GlobalAction.ManageEntityTypes,
    GlobalAction.ManagePermissionGrants,
    GlobalAction.ManageAuthentication,
    GlobalAction.ViewAuditLog
  )

  const { error } = useEntityTypes()
  const entityTypes = useSortedEntityTypes() ?? []

  const shell = (content: ReactNode, sections?: NavSection[]) => (
    <AppShell
      headerHeight={64}
      headerWithBorder={false}
      header={
        <TopAppBar
          userEntityId={userEntityId ? entityIdFromString(userEntityId) : undefined}
          signOut={() => auth.signoutRedirect()}
        />
      }
      navbar={sections && <NavbarSections sections={sections} />}
      navbarOpened={drawerOpen}
      onNavbarClose={closeDrawer}
    >
      {content}
    </AppShell>
  )

  if (error) {
    return shell(
      <Collapse expanded={!!error}>
        <Alert
          variant="filled"
          severity="negative"
          m="md"
          title="Failed to load entities. Please try again."
          action={
            <Button
              variant="white"
              size="xs"
              onClick={() => queryClient.invalidateQueries({ queryKey: entityTypeKeys.all })}
            >
              Refresh
            </Button>
          }
        >
          <div>{String(error)}</div>
        </Alert>
      </Collapse>
    )
  }

  if (fetchPermissionsError) {
    return shell(
      <Collapse expanded={!!fetchPermissionsError}>
        <Alert
          variant="filled"
          severity="negative"
          m="md"
          title="Failed to load permissions. Please reload the page."
          action={
            <Button variant="white" size="xs" onClick={async () => window.location.reload()}>
              Refresh
            </Button>
          }
        >
          <div>{String(fetchPermissionsError)}</div>
        </Alert>
      </Collapse>
    )
  }

  const createEntityTypeItem = (
    entityType: EntityType,
    icon = iconForEntityTypeKind(entityType.kind, <IconList size={iconSizes.sm} stroke={1.5} />)
  ): NavItem => ({
    icon,
    text: entityType.name,
    key: `type-${entityType.id.toString()}`,
    path: `/entities/${entityType.id.toString()}`,
  })

  const canManageEntityTypes = hasGlobalPermission(GlobalAction.ManageEntityTypes)
  const canManagePermissionGrants = hasGlobalPermission(GlobalAction.ManagePermissionGrants)
  const canManageAdminUsers = hasGlobalPermission(GlobalAction.ManageAuthentication)
  const canViewAuditLog = hasGlobalPermission(GlobalAction.ViewAuditLog)

  const adminUsersEntityType = entityTypes.find(
    (type) => type.kind.equals(EntityTypeKind.Companion.User) && isAdminUserType(type.name)
  )

  const adminItems: NavItem[] = []
  if (canManageEntityTypes) {
    adminItems.push({
      icon: <IconTopologyStar3 size={iconSizes.sm} stroke={1.5} />,
      text: 'Types',
      key: 'types',
      path: `/types`,
    })
    adminItems.push({
      icon: <IconBrain size={iconSizes.sm} stroke={1.5} />,
      text: 'Semantics',
      key: 'semantics',
      path: `/semantics`,
    })
  }
  if (canManagePermissionGrants) {
    adminItems.push({
      icon: <IconLock size={iconSizes.sm} stroke={1.5} />,
      text: 'Access Control',
      key: 'access-control',
      path: `/access-control`,
    })
  }
  adminItems.push({
    icon: <IconWorld size={iconSizes.sm} stroke={1.5} />,
    text: 'Asset Registry',
    key: 'registry',
    path: `/registry`,
  })
  if (canViewAuditLog) {
    adminItems.push({
      icon: <IconHistoryToggle size={iconSizes.sm} stroke={1.5} />,
      text: 'Audit Log',
      key: 'audit',
      path: `/audit`,
    })
  }
  if (canManageAdminUsers && adminUsersEntityType) {
    adminItems.push({
      icon: <IconShieldLock size={iconSizes.sm} stroke={1.5} />,
      text: 'Admin Users',
      key: `admin-user-${adminUsersEntityType.id.toString()}`,
      path: `/entities/${adminUsersEntityType.id.toString()}`,
    })
  }

  const adminEntityTypeId = adminUsersEntityType?.id.toString()
  const visibleEntityTypes = adminEntityTypeId
    ? entityTypes.filter((type) => type.id.toString() !== adminEntityTypeId)
    : entityTypes

  const kindSections = buildSidebarSectionsFromEntityTypes(visibleEntityTypes, createEntityTypeItem)

  const sections: NavSection[] = []

  if (adminItems.length) {
    sections.push({
      id: 'administration',
      title: 'Administration',
      items: adminItems,
      dividerBefore: false,
    })
  }

  for (const sidebarGroup of SIDEBAR_GROUP_ORDER) {
    const groupSections = kindSections.filter((s) => s.sidebarGroup === sidebarGroup && s.items.length > 0)
    groupSections.forEach(({ id, title, items }, index) => {
      sections.push({ id, title, items, dividerBefore: index === 0 && sections.length > 0 })
    })
  }

  return shell(<Outlet />, sections)
}

export default LandingPage
